/**
 * «Múdate en un clic» (plan 10b, A1) y reputación portátil (A2): lo puro y compartido.
 *
 * Fuentes por país (lo que de verdad se puede leer sin cuenta, comprobado el 13/10/2026):
 *   - Vinted (toda Europa): API interna del armario (1.0 extractVintedProfile.js) — a veces la
 *     bloquea DataDome desde IPs de servidor.
 *   - Jófogás (HU): la lista «más anuncios del vendedor» (`?account_list_id=`) y cada anuncio
 *     (Next.js, `__NEXT_DATA__` con subject/body/price/images).
 *   - Wallapop (ES): la página pública del usuario trae en el HTML sus anuncios y `userStats`;
 *     cada anuncio tiene JSON-LD Product + og:. La API (`api.wallapop.com`) devuelve 403.
 *   - Depop / Facebook Marketplace: sin acceso público (403 / login) → enlaces de anuncios de
 *     uno en uno (cualquier página con JSON-LD Product u og:) y CSV.
 */
import { z } from 'zod';
import { PRODUCT_CONDITIONS } from '../firestore/products.js';
import {
  CURRENCIES,
  CurrencySchema,
  TimestampSchema,
  UserIdSchema,
} from '../firestore/primitives.js';

export const IMPORT_SOURCES = ['vinted', 'wallapop', 'jofogas', 'depop', 'other'] as const;
export const ImportSourceSchema = z.enum(IMPORT_SOURCES);
export type ImportSource = z.infer<typeof ImportSourceSchema>;

/** Máximo de anuncios por extracción (1.0: 150). */
export const MAX_IMPORT_ITEMS = 150;
/** Enlaces pegados a mano por petición. */
export const MAX_IMPORT_URLS = 60;

/** Plataforma principal de cada país (plan 10b: «cada país tiene una plataforma de donde mudarse»). */
export const IMPORT_SOURCES_BY_COUNTRY: Readonly<Record<string, readonly ImportSource[]>> = {
  HU: ['jofogas', 'vinted'],
  ES: ['wallapop', 'vinted'],
  IT: ['vinted', 'other'],
  FR: ['vinted', 'other'],
  DE: ['vinted', 'other'],
  AT: ['vinted', 'other'],
  PT: ['vinted', 'other'],
  NL: ['vinted', 'other'],
  BE: ['vinted', 'other'],
  PL: ['vinted', 'other'],
  CZ: ['vinted', 'other'],
  SK: ['vinted', 'other'],
  RO: ['vinted', 'other'],
  LT: ['vinted', 'other'],
};
export const DEFAULT_IMPORT_SOURCES: readonly ImportSource[] = ['vinted', 'other'];

export function importSourcesFor(country: string | null | undefined): readonly ImportSource[] {
  const list = country ? IMPORT_SOURCES_BY_COUNTRY[country.toUpperCase()] : undefined;
  return list ?? DEFAULT_IMPORT_SOURCES;
}

/** Fuente por el host de la URL (perfil o anuncio). */
export function detectImportSource(url: string): ImportSource {
  let host = '';
  try {
    host = new URL(url.startsWith('http') ? url : `https://${url}`).hostname.toLowerCase();
  } catch {
    return 'other';
  }
  if (/^(www\.)?vinted\.[a-z]{2,3}(\.[a-z]{2})?$/.test(host)) return 'vinted';
  if (/(^|\.)wallapop\.com$/.test(host)) return 'wallapop';
  if (/(^|\.)jofogas\.hu$/.test(host)) return 'jofogas';
  if (/(^|\.)depop\.com$/.test(host)) return 'depop';
  return 'other';
}

/** URL de perfil (lista de anuncios) frente a URL de un anuncio suelto. */
export function isProfileUrl(url: string, source: ImportSource): boolean {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return false;
  }
  switch (source) {
    case 'vinted':
      return /\/member\/\d+/.test(u.pathname);
    case 'wallapop':
      return /^\/(app\/)?user\//.test(u.pathname);
    case 'jofogas':
      return u.searchParams.has('account_list_id');
    case 'depop':
      return /^\/[a-z0-9_.-]+\/?$/i.test(u.pathname) && !u.pathname.startsWith('/products/');
    case 'other':
      return false;
  }
}

export const ImportedItemSchema = z.object({
  source: ImportSourceSchema,
  external_id: z.string().max(120),
  url: z.string().url().max(2000),
  title: z.string().max(120),
  description: z.string().max(4000),
  price: z.number().min(0),
  currency: CurrencySchema.nullable(),
  photos: z.array(z.string().url()).max(50),
  condition: z.enum(PRODUCT_CONDITIONS).nullable(),
  brand: z.string().max(60),
  size: z.string().max(12),
  /** Nombre de categoría tal como lo da la plataforma (pista para la IA). */
  category_hint: z.string().max(200),
});
export type ImportedItem = z.infer<typeof ImportedItemSchema>;

export const IMPORT_JOB_STATUSES = ['review', 'publishing', 'done', 'failed'] as const;
export const ImportResultSchema = z.object({
  external_id: z.string(),
  product_id: z.string().nullable(),
  status: z.string().nullable(),
  error: z.string().nullable(),
});
export type ImportResult = z.infer<typeof ImportResultSchema>;

/** `importJobs/{id}`: lo extraído (para revisar), la selección y el progreso de publicación. */
export const ImportJobSchema = z.object({
  uid: UserIdSchema,
  source: ImportSourceSchema,
  profile_url: z.string().max(2000),
  status: z.enum(IMPORT_JOB_STATUSES),
  items: z.array(ImportedItemSchema).max(MAX_IMPORT_ITEMS),
  selected: z.array(z.string()).max(MAX_IMPORT_ITEMS),
  language: z.string().regex(/^[a-z]{2}$/),
  results: z.array(ImportResultSchema),
  done_count: z.number().int().min(0),
  error: z.string().nullable(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type ImportJob = z.infer<typeof ImportJobSchema>;

/** A2: sello «320 ventas y 4,9 en Vinted» mientras no haya historial propio. */
export const ExternalReputationSchema = z.object({
  source: ImportSourceSchema,
  profile_url: z.string().max(2000),
  external_id: z.string().max(120),
  handle: z.string().max(80),
  sales_count: z.number().int().min(0).nullable(),
  reviews_count: z.number().int().min(0).nullable(),
  /** 0–5, null si la plataforma no lo da. */
  rating: z.number().min(0).max(5).nullable(),
  /** Código `nuvvu-xxxxxx` que el usuario pone en la bio de la otra plataforma para demostrar que es suya. */
  verification_code: z.string().max(20),
  verified_at: TimestampSchema.nullable(),
  checked_at: TimestampSchema,
});
export type ExternalReputation = z.infer<typeof ExternalReputationSchema>;

/** Hasta cuántas valoraciones propias se enseña el sello externo (plan A2: «hasta que tenga historial»). */
export const EXTERNAL_REPUTATION_UNTIL_OWN_REVIEWS = 10;

export function externalReputationBadge(
  r: {
    verified: boolean;
    sales_count: number | null;
    reviews_count: number | null;
    rating: number | null;
  },
  ownReviews: number,
): boolean {
  return (
    r.verified &&
    ownReviews < EXTERNAL_REPUTATION_UNTIL_OWN_REVIEWS &&
    ((r.sales_count ?? 0) > 0 || (r.reviews_count ?? 0) > 0 || r.rating !== null)
  );
}

// ---------- parsers puros (HTML público → ImportedItem parcial) ----------

const norm = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/**
 * Estado de Vinted (texto en cualquier idioma) → condición nuvvu (1.0 COND_RULES; el orden
 * importa: «muy bueno» antes que «bueno»). Jófogás y Wallapop no exponen estado público.
 */
const COND_RULES: readonly [ImportedItem['condition'], readonly string[]][] = [
  [
    'new',
    [
      'con etiqueta',
      'with tags',
      'avec etiquette',
      'mit etikett',
      'con cartellino',
      'com etiqueta',
      'met prijskaartje',
      'z metka',
      'cimkevel',
      'nowy z metka',
    ],
  ],
  [
    'like_new',
    [
      'sin etiqueta',
      'without tags',
      'sans etiquette',
      'ohne etikett',
      'senza cartellino',
      'sem etiqueta',
      'zonder prijskaartje',
      'bez metki',
      'muy buen',
      'muy bueno',
      'very good',
      'tres bon',
      'sehr gut',
      'ottime',
      'muito bom',
      'zeer goed',
      'bardzo dobry',
      'cimke nelkul',
      'nagyon jo',
    ],
  ],
  [
    'fair',
    [
      'satisf',
      'aceptable',
      'acceptable',
      'zufrieden',
      'soddisfac',
      'discrete',
      'redelijk',
      'bevredigend',
      'zadowalaj',
      'megfelelo',
    ],
  ],
  ['good', ['buen', 'bueno', 'good', 'bon', 'gut', 'buone', 'bom', 'goed', 'dobry', 'jo allapot']],
];
const COND_BY_STATUS_ID: Record<number, ImportedItem['condition']> = {
  1: 'new',
  2: 'like_new',
  3: 'like_new',
  4: 'good',
  5: 'fair',
  6: 'new',
};

export function mapVintedCondition(status: unknown): ImportedItem['condition'] {
  if (status === null || status === undefined) return null;
  if (typeof status === 'number') return COND_BY_STATUS_ID[status] ?? null;
  if (typeof status !== 'string') return null;
  const s = norm(status.trim());
  if (!s) return null;
  if (/^\d+$/.test(s)) return COND_BY_STATUS_ID[Number(s)] ?? null;
  for (const [cond, pats] of COND_RULES) if (pats.some((p) => s.includes(p))) return cond;
  return null;
}

/** schema.org itemCondition (JSON-LD de Wallapop y otros) → condición nuvvu. */
export function mapSchemaCondition(value: unknown): ImportedItem['condition'] {
  const s = typeof value === 'string' ? value.toLowerCase() : '';
  if (s.includes('newcondition')) return 'new';
  if (s.includes('refurbished')) return 'like_new';
  if (s.includes('usedcondition')) return 'good';
  if (s.includes('damaged')) return 'fair';
  return null;
}

const decodeEntities = (s: string): string =>
  s
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, n: string) => String.fromCodePoint(Number(n)))
    .replace(/&#x([0-9a-f]+);/gi, (_, n: string) => String.fromCodePoint(parseInt(n, 16)));

/** `<meta property|name="og:x" content="…">` → { 'og:x': '…' } (sin importar el orden de atributos). */
export function parseOgTags(html: string): Record<string, string> {
  const out: Record<string, string> = {};
  const re =
    /<meta\s+[^>]*?(?:property|name)=["']((?:og|twitter|product):[a-z:_]+|description)["'][^>]*>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    const tag = m[0];
    const key = (m[1] ?? '').toLowerCase();
    const c = /content=["']([^"']*)["']/i.exec(tag);
    if (c?.[1] && out[key] === undefined) out[key] = decodeEntities(c[1]).trim();
  }
  return out;
}

export interface JsonLdProduct {
  name: string;
  description: string;
  images: string[];
  price: number | null;
  currency: string | null;
  brand: string;
  condition: ImportedItem['condition'];
  url: string;
}

const asStr = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

function findProduct(node: unknown, depth = 0): Record<string, unknown> | null {
  if (!isObj(node) || depth > 6) return null;
  if (Array.isArray(node)) {
    for (const el of node) {
      const f = findProduct(el, depth + 1);
      if (f) return f;
    }
    return null;
  }
  const type = node['@type'];
  if (type === 'Product' || (Array.isArray(type) && type.includes('Product'))) return node;
  for (const key of ['@graph', 'mainEntity', 'itemListElement', 'item']) {
    const f = findProduct(node[key], depth + 1);
    if (f) return f;
  }
  return null;
}

/** Primer `Product` de los bloques `<script type="application/ld+json">` de la página. */
export function parseJsonLdProduct(html: string): JsonLdProduct | null {
  const re = /<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    let parsed: unknown;
    try {
      parsed = JSON.parse((m[1] ?? '').trim());
    } catch {
      continue;
    }
    const p = findProduct(parsed);
    if (!p) continue;
    const offers: unknown = Array.isArray(p.offers) ? p.offers[0] : p.offers;
    const o: Record<string, unknown> = isObj(offers) ? offers : {};
    const rawImg = p.image;
    const images = (Array.isArray(rawImg) ? rawImg : [rawImg])
      .map((i) => (isObj(i) ? asStr(i.url ?? i.contentUrl) : asStr(i)))
      .filter((u) => /^https?:\/\//.test(u));
    const priceRaw: unknown = o.price ?? p.price;
    const price =
      typeof priceRaw === 'number'
        ? priceRaw
        : typeof priceRaw === 'string'
          ? Number(priceRaw.replace(',', '.'))
          : NaN;
    const brand = isObj(p.brand) ? asStr(p.brand.name) : asStr(p.brand);
    return {
      name: asStr(p.name),
      description: asStr(p.description),
      images,
      price: Number.isFinite(price) && price > 0 ? price : null,
      currency: /^[A-Z]{3}$/.test(asStr(o.priceCurrency)) ? asStr(o.priceCurrency) : null,
      brand: brand === 'null' ? '' : brand,
      condition: mapSchemaCondition(o.itemCondition ?? p.itemCondition),
      url: asStr(o.url) || asStr(p.url),
    };
  }
  return null;
}

/** Precio de un texto og:description tipo «por 7 EUR» / «5000 Ft-ért» (último recurso). */
export function priceFromText(text: string): { price: number; currency: string } | null {
  const m =
    /(\d{1,3}(?:[ \u00a0.]\d{3})+|\d+(?:[.,]\d{1,2})?)\s*(EUR|€|HUF|Ft|PLN|zł|CZK|Kč|RON|lei|SEK|DKK|NOK|kr|GBP|£)/i.exec(
      text,
    );
  if (!m?.[1] || !m[2]) return null;
  const raw = m[1];
  const price =
    /[ \u00a0.]\d{3}/.test(raw) && !/,\d{1,2}$/.test(raw)
      ? Number(raw.replace(/[ \u00a0.]/g, ''))
      : Number(raw.replace(',', '.'));
  const cur: Record<string, string> = {
    '€': 'EUR',
    ft: 'HUF',
    zł: 'PLN',
    kč: 'CZK',
    lei: 'RON',
    kr: 'SEK',
    '£': 'GBP',
  };
  const currency = cur[m[2].toLowerCase()] ?? m[2].toUpperCase();
  return Number.isFinite(price) && price > 0 ? { price, currency } : null;
}

const isCurrency = (c: string | null): c is ImportedItem['currency'] & string =>
  c !== null && (CURRENCIES as readonly string[]).includes(c);

/** Combina JSON-LD y og: en un ImportedItem genérico (cualquier marketplace con SEO). */
export function itemFromHtml(
  source: ImportSource,
  url: string,
  html: string,
  externalId: string,
): ImportedItem | null {
  const ld = parseJsonLdProduct(html);
  const og = parseOgTags(html);
  const firstText = (...vals: (string | undefined)[]): string => vals.find((v) => v) ?? '';
  const title = firstText(ld?.name, og['og:title']).replace(/\s+/g, ' ').trim().slice(0, 120);
  if (!title) return null;
  const fromText = priceFromText(`${og['og:description'] ?? ''} ${og.description ?? ''}`);
  const price = ld?.price ?? fromText?.price ?? 0;
  const currency = ld?.currency ?? fromText?.currency ?? null;
  const photos = [...new Set([...(ld?.images ?? []), ...(og['og:image'] ? [og['og:image']] : [])])];
  return {
    source,
    external_id: externalId.slice(0, 120),
    url,
    title,
    description: firstText(ld?.description, og['og:description']).trim().slice(0, 4000),
    price,
    currency: isCurrency(currency) ? currency : null,
    photos: photos.slice(0, 50),
    condition: ld?.condition ?? null,
    brand: (ld?.brand ?? '').slice(0, 60),
    size: '',
    category_hint: '',
  };
}

/** Código de verificación de propiedad del perfil externo (A2). */
export function verificationCode(uid: string): string {
  let h = 0;
  for (const ch of uid) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return `nuvvu-${h.toString(36).padStart(6, '0').slice(-6)}`;
}
